/**
 * PulseBanner - Shows value metrics like time saved
 * 
 * Displays:
 * - Time saved this week/month
 * - Tasks automated
 * - Emails handled
 * - Meetings scheduled
 * 
 * Builds trust by showing tangible value.
 */
import React, { useState, useEffect } from 'react';
import { getIdToken } from '../services/firebase';
import { Clock, Zap, Mail, Calendar, TrendingUp, ChevronRight, X } from './shared';

// =============================================================================
// TYPES
// =============================================================================

interface PulseMetric {
  id: string;
  label: string;
  value: string | number;
  unit?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

interface PulseBannerProps {
  userId?: string;
  onDismiss?: () => void;
  compact?: boolean;
}

// =============================================================================
// EMPTY STATE (shown when no real metrics exist yet)
// =============================================================================

const getEmptyStateMetric = (): PulseMetric[] => [
  {
    id: 'getting-started',
    label: 'Start using SuperJarvis to track your progress',
    value: '→',
    icon: Zap,
    trend: 'neutral'
  }
];

// =============================================================================
// COMPACT BANNER (Single line, dismissible)
// =============================================================================

export const PulseBannerCompact: React.FC<PulseBannerProps> = ({
  onDismiss
}) => {
  const [visible, setVisible] = useState(true);
  const [metrics, setMetrics] = useState<PulseMetric[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // Check if banner was dismissed
    const dismissed = localStorage.getItem('superjarvis_pulse_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 24 hours
      if (Date.now() - dismissedTime < 24 * 60 * 60 * 1000) {
        setVisible(false);
        return;
      }
    }
    
    // Fetch real metrics from API
    const fetchMetrics = async () => {
      try {
        const API_BASE = import.meta.env.VITE_API_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
        const token = await getIdToken();
        if (!token) return;
        const response = await fetch(`${API_BASE}/api/metrics?period=week`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Map API metrics to component format
          const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
            clock: Clock,
            zap: Zap,
            mail: Mail,
            calendar: Calendar
          };
          
          const apiMetrics = (data.metrics || [])
            .filter((m: any) => m.value > 0)  // Only show non-zero metrics
            .map((m: any) => ({
              ...m,
              icon: iconMap[m.icon] || Clock
            }));
          
          if (apiMetrics.length > 0) {
            setMetrics(apiMetrics);
            return;
          }
        }
      } catch (error) {
        console.warn('Metrics API unavailable, using defaults');
      }
      
      // Fallback to mock metrics for new users
      setMetrics(getEmptyStateMetric());
    };
    
    fetchMetrics();
  }, []);

  // Rotate through metrics
  useEffect(() => {
    if (metrics.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % metrics.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [metrics.length]);

  const handleDismiss = () => {
    localStorage.setItem('superjarvis_pulse_dismissed', Date.now().toString());
    setVisible(false);
    onDismiss?.();
  };

  if (!visible || metrics.length === 0) return null;

  const currentMetric = metrics[currentIndex];

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="flex items-center gap-3 px-4 py-2 bg-black/80 backdrop-blur-xl border border-cyan-400/30 rounded-full shadow-[0_0_30px_rgba(34,211,238,0.2)]">
        {/* Icon */}
        <div className="p-1.5 rounded-full bg-cyan-400/20">
          <currentMetric.icon className="w-4 h-4 text-cyan-400" />
        </div>
        
        {/* Metric */}
        <div className="flex items-baseline gap-1.5">
          <span className="text-cyan-300 font-semibold">
            {currentMetric.value}
            {currentMetric.unit && <span className="text-xs ml-0.5">{currentMetric.unit}</span>}
          </span>
          <span className="text-cyan-400/60 text-sm">{currentMetric.label}</span>
        </div>

        {/* Trend indicator */}
        {currentMetric.trend === 'up' && (
          <TrendingUp className="w-4 h-4 text-green-400" />
        )}

        {/* Progress dots */}
        <div className="flex gap-1 ml-2">
          {metrics.map((_, i) => (
            <div
              key={i}
              className={`w-1 h-1 rounded-full transition-colors ${
                i === currentIndex ? 'bg-cyan-400' : 'bg-cyan-400/30'
              }`}
            />
          ))}
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-cyan-400/10 rounded-full transition-colors ml-1"
        >
          <X className="w-3 h-3 text-cyan-400/50 hover:text-cyan-300" />
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// FULL BANNER (Expanded stats panel)
// =============================================================================

export const PulseBannerFull: React.FC<PulseBannerProps> = ({
  compact = false
}) => {
  const [metrics, setMetrics] = useState<PulseMetric[]>([]);

  useEffect(() => {
    setMetrics(getEmptyStateMetric());
  }, []);

  if (metrics.length === 0) return null;

  return (
    <div className={`w-full ${compact ? 'max-w-xl' : 'max-w-4xl'} mx-auto`}>
      <div className="bg-black/40 backdrop-blur-xl border border-cyan-400/20 rounded-2xl p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-cyan-400/20">
              <TrendingUp className="w-4 h-4 text-cyan-400" />
            </div>
            <h3 className="text-cyan-300 font-semibold text-sm">Your Impact</h3>
          </div>
          <button className="text-xs text-cyan-400/50 hover:text-cyan-300 flex items-center gap-1 transition-colors">
            View details
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Metrics Grid */}
        <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-4'} gap-4`}>
          {metrics.map((metric) => (
            <div
              key={metric.id}
              className="flex flex-col items-center p-3 bg-cyan-400/5 rounded-xl border border-cyan-400/10"
            >
              <metric.icon className="w-5 h-5 text-cyan-400/60 mb-2" />
              <div className="text-2xl font-bold text-cyan-300">
                {metric.value}
                {metric.unit && <span className="text-sm ml-0.5">{metric.unit}</span>}
              </div>
              <div className="text-xs text-cyan-400/50 text-center mt-1">
                {metric.label}
              </div>
              {metric.trendValue && (
                <div className="text-xs text-green-400/70 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {metric.trendValue}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// INLINE STAT (For use in headers or sidebars)
// =============================================================================

interface InlineStatProps {
  label: string;
  value: string | number;
  unit?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export const InlineStat: React.FC<InlineStatProps> = ({
  label,
  value,
  unit,
  icon: Icon = Clock
}) => {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-400/5 rounded-lg border border-cyan-400/10">
      <Icon className="w-4 h-4 text-cyan-400/60" />
      <span className="text-cyan-300 font-medium">
        {value}
        {unit && <span className="text-xs ml-0.5 text-cyan-400/60">{unit}</span>}
      </span>
      <span className="text-xs text-cyan-400/50">{label}</span>
    </div>
  );
};

export default PulseBannerCompact;
